// Ось времени ролика: он перестал быть циклом кометы и стал прогоном по суткам.
//
// Здесь только раскладка (какой кадр за какую дату отвечает) и выбор окна записи.
// Карта, кодировщик и снос об этом модуле не знают — им приходят готовые числа.
//
// См. docs/plans/2026-08-16-video-drift-timeline-design.md

import { dateFromOffset } from '../models/driftDay'
import { VIDEO_FRAMES } from './exportVideo'

/** Кадров на сутки — целевая плотность. 18 при 30 к/с даёт 0,6 с на дату:
 *  дату успеваешь прочесть, а десять суток укладываются в шесть секунд. */
export const FRAMES_PER_DAY = 18

/** Сколько кадров отвести окну из `days` суток.
 *
 *  Округляем до ЦЕЛОГО ЧИСЛА комет-циклов: фазы комет считаются от номера кадра
 *  (см. exportVideo), и ролик, оборвавшийся посреди пролёта, показал бы на стыке
 *  рывок — ровно тот, ради устранения которого фазу и задаёт запись.
 *
 *  Ниже одного цикла не опускаемся никогда: на одних сутках кометы обязаны
 *  пролететь целиком, иначе получатель прочитает ролик как поломку. */
export function frameCount(days: number, framesPerDay: number = FRAMES_PER_DAY): number {
  const wanted = Math.max(1, days) * framesPerDay
  const cycles = Math.max(1, Math.round(wanted / VIDEO_FRAMES))
  return cycles * VIDEO_FRAMES
}

/** Что рисовать в кадре. Всё выведено из номера кадра.
 *
 *  Времени здесь по-прежнему нет: доля суток — это положение кадра, а не час.
 *  Час на подписи получается из неё уже в таймере и только на шестичасовом поле
 *  (`exportTimer.sliceHour`): раскладка ролика об источнике течений не знает и
 *  знать не должна. */
export interface VideoFrameSpec {
  /** Целые сутки от сегодня: по ним стоит таймер и растровый слой. */
  dayOffset: number
  /** Доля суток [0,1): по ней едут точки сноса и их ореолы. */
  frac: number
  /** Фаза кометы [0,1). */
  phase: number
}

/** Раскладка всего ролика: кадр за кадром от `from` до `to` включительно. */
export function framePlan(
  from: number,
  to: number,
  framesPerDay: number = FRAMES_PER_DAY,
): VideoFrameSpec[] {
  const days = Math.max(1, to - from + 1)
  const total = frameCount(days, framesPerDay)
  const out: VideoFrameSpec[] = []
  for (let k = 0; k < total; k++) {
    // Последний кадр не дотягивает до конца последних суток — как и фаза 1 не
    // входит в цикл кометы: иначе он показал бы уже следующую дату.
    const dayFloat = from + (days * k) / total
    const dayOffset = Math.floor(dayFloat)
    out.push({
      dayOffset,
      frac: dayFloat - dayOffset,
      phase: (k % VIDEO_FRAMES) / VIDEO_FRAMES,
    })
  }
  return out
}

/** Готовность одной даты окна. */
export interface DayReadiness {
  offset: number
  /** Слой на эту дату будет в кадре: тайлы готовы или их отрисуют. */
  ok: boolean
}

export interface RecordWindow {
  from: number
  to: number
}

/** Какое окно писать: НЕПРЕРЫВНЫЙ кусок, содержащий текущую дату.
 *
 *  Не самый длинный: перескок таймера через сутки читается как поломка записи, а
 *  ролик, начинающийся не там, где стоит человек, отвечает не на его вопрос.
 *  Текущая дата не готова — писать нечего, и это честнее подмены соседней. */
export function recordWindow(days: DayReadiness[], current: number): RecordWindow | null {
  const here = days.find(d => d.offset === current)
  if (!here?.ok) return null
  const ready = new Set(days.filter(d => d.ok).map(d => d.offset))
  let from = current
  let to = current
  while (ready.has(from - 1)) from--
  while (ready.has(to + 1)) to++
  return { from, to }
}

/** Края окна в датах — их и называет кнопка, чтобы человек не решил, будто
 *  ролик обязан был накрыть всё окно перемотки. */
export function windowLabelRange(
  win: RecordWindow,
  today: string,
): { from: string; to: string } {
  return {
    from: dateFromOffset(win.from, today),
    to: dateFromOffset(win.to, today),
  }
}
